/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute, type RouteProp, type ParamListBase } from '@react-navigation/native';
import { formatIsoDate, formatLocalDateTime } from '@shared/vueltoDateTime';
import { formatMoney } from '../../shared/format/public-api';
import {
  getQuote,
  listMessages,
  sendStoreMessage,
  markReadByStore,
  setStatus,
  issueQuote,
  acceptAsStore,
  getQuotePhotoSignedUrl,
  createWalkInSessions,
  listQuoteAppointments,
  getQuoteEvaluationAppointment,
} from '../infra/quotesRepo';
import type {
  ServiceQuoteMessage,
  ServiceQuotePlanPaymentMode,
  ServiceQuotePricingMode,
  ServiceQuoteRequest,
} from '../domain/types';
import { computeSessionProgress, formatScheduleSessionCta, nextSessionIndex } from '../domain/sessionProgress';
import { quoteServiceDisplayName } from '../domain/quoteDisplay';
import { listResources } from '../../appointments/infra/appointmentsRepo';
import type { Appointment, AppointmentResource, AppointmentStatus } from '../../appointments/domain/types';
import { HmTimeField } from '../../appointments/components/HmTimeField';
import { ZoomableImage } from '../../consultations/public-api';
import { formatArPhoneDisplay } from '@shared/phone';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField'
import { showError, showSuccess } from '../../shared/ui/appToast';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { ticketColors } from '../../shared/theme/ticketColors';
import { colors } from '../../shared/theme/colors';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { brand, status as statusTokens } from '@vuelto/tokens';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';

const APPT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  requested: 'Solicitado',
  confirmed: 'Confirmado',
  completed: 'Completado',
  cancelled: 'Cancelado',
  no_show: 'No vino',
};

const STATUS_LABELS: Record<string, string> = {
  requested: 'Solicitada',
  needs_info: 'Pide más info',
  visit_proposed: 'Evaluación propuesta',
  visit_scheduled: 'Evaluación agendada',
  quoted: 'Cotizada',
  accepted: 'Aceptada',
  scheduled: 'Agendada',
  completed: 'Completada',
  declined: 'Rechazada',
  expired: 'Vencida',
  cancelled: 'Cancelada',
};

const STATUS_COLORS: Record<string, string> = {
  requested: statusTokens.consultationPending,
  needs_info: statusTokens.consultationPending,
  visit_proposed: statusTokens.consultationAnswered,
  visit_scheduled: statusTokens.consultationAnswered,
  quoted: statusTokens.consultationAnswered,
  accepted: statusTokens.consultationAnswered,
  scheduled: statusTokens.consultationAnswered,
  completed: statusTokens.consultationClosed,
  declined: statusTokens.cancelled,
  expired: statusTokens.consultationClosed,
  cancelled: statusTokens.cancelled,
};

const TERMINAL = new Set(['completed', 'declined', 'expired', 'cancelled']);

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toHm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Combine YYYY-MM-DD + HH:mm into a local Date (counter walk-in). */
function combineLocalDateAndTime(isoDate: string, hm: string): Date | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
  if (!dateMatch || !timeMatch) return null;
  const y = Number(dateMatch[1]);
  const mo = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const h = Number(timeMatch[1]);
  const min = Number(timeMatch[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  const d = new Date(y, mo - 1, day, h, min, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function QuoteDetailScreen() {
  const route = useRoute<RouteProp<ParamListBase, 'QuoteDetail'>>();
  const navigation = useNavigation<any>();
  const quoteId = (route.params as { quoteId: string }).quoteId;
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const canEdit = useModulePermission('BranchSettings', 'edit');
  const canSend = useModulePermission('BranchSettings', 'send');
  const canConvert = useModulePermission('BranchSettings', 'convert');
  const canReply = useModulePermission('BranchSettings', 'reply');
  const hasWorkOrdersCap = useHasBusinessCapability('work_orders');
  const capabilityGate = useCapabilityGate('service_quotes', {
    title: 'Presupuestos no disponibles',
    subtitle: 'Este rubro no tiene presupuestos.',
  });
  const [quote, setQuote] = useState<ServiceQuoteRequest | null>(null);
  const [messages, setMessages] = useState<ServiceQuoteMessage[]>([]);
  const [sessionAppts, setSessionAppts] = useState<Appointment[]>([]);
  const [evalAppt, setEvalAppt] = useState<Appointment | null>(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [showIssue, setShowIssue] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  // Issue form
  const [pricingMode, setPricingMode] = useState<ServiceQuotePricingMode>('per_session');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('60');
  const [sessions, setSessions] = useState('1');
  const [quoteNote, setQuoteNote] = useState('');
  const [planPaymentMode, setPlanPaymentMode] = useState<ServiceQuotePlanPaymentMode>('inherit');
  const [planDepositAmount, setPlanDepositAmount] = useState('');

  // Schedule modal
  const [resources, setResources] = useState<AppointmentResource[]>([]);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('10:00');
  const [resourcePickerOpen, setResourcePickerOpen] = useState(false);

  useEffect(() => {
    if (showIssue) {
      setPricingMode('per_session');
      setPrice('');
      setDuration('60');
      setSessions('1');
      setQuoteNote('');
      setPlanPaymentMode('inherit');
      setPlanDepositAmount('');
    }
  }, [showIssue]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [q, msgs, appts] = await Promise.all([
        getQuote(quoteId),
        listMessages(quoteId),
        listQuoteAppointments(quoteId),
      ]);
      setQuote(q);
      setMessages(msgs);
      setSessionAppts(appts);

      if (q) {
        const evaluation = await getQuoteEvaluationAppointment(q);
        setEvalAppt(evaluation);
      } else {
        setEvalAppt(null);
      }

      if (q?.photo_urls?.length) {
        const urlResults = await Promise.all(
          q.photo_urls.map(async (path) => {
            if (path.startsWith('http')) return { ok: true, url: path } as const;
            const signed = await getQuotePhotoSignedUrl(path);
            return signed ? ({ ok: true, url: signed } as const) : ({ ok: false, path } as const);
          }),
        );
        setPhotoUrls(urlResults.filter((r) => r.ok).map((r) => (r as { ok: true; url: string }).url));
        const failed = urlResults.filter((r) => !r.ok).length;
        if (failed > 0) {
          showError('Fotos', `No se pudo cargar ${failed} foto${failed === 1 ? '' : 's'}.`);
        }
      } else {
        setPhotoUrls([]);
      }

      if (q && !q.read_by_store_at) {
        try {
          await markReadByStore(quoteId);
        } catch (e) {
          console.error('QuoteDetailScreen markReadByStore', e);
        }
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'No se pudo cargar la cotización.';
      console.error('QuoteDetailScreen load', e);
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 100);
    return () => clearTimeout(id);
  }, [messages]);

  const resourceOptions = useMemo(
    () => resources.map((r) => ({ id: r.id, name: r.displayName })),
    [resources],
  );
  const selectedResource = resources.find((r) => r.id === resourceId) ?? null;
  const singleAgenda = resources.length === 1;

  const run = async (fn: () => Promise<void>, errLabel: string) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (e) {
      presentUnexpectedFailure({ module: 'quotes', op: 'quote-action', error: e, title: 'Cotización', userMessage: errLabel });
    } finally {
      setBusy(false);
    }
  };

  const handleReply = () =>
    void run(async () => {
      if (!canReply) {
        showError('Sin permiso', 'No podés enviar mensajes en cotizaciones.');
        return;
      }
      if (reply.trim().length < 1) {
        showError('Mensaje', 'Escribí un mensaje para enviar.');
        return;
      }
      await sendStoreMessage(quoteId, reply.trim());
      setReply('');
      showSuccess('Mensaje enviado');
    }, 'Error al enviar.');

  const handleNeedsInfo = () =>
    void run(async () => {
      if (!canEdit) {
        showError('Sin permiso', 'No podés cambiar el estado de la cotización.');
        return;
      }
      // ponytail: fixed copy; free-text lives in the reply bar
      await setStatus(quoteId, 'needs_info', { message: 'Mandame un poco más de info.' });
      showSuccess('Le pediste más info');
    }, 'No se pudo pedir info.');

  const handleProposeVisit = () =>
    void run(async () => {
      if (!canEdit) {
        showError('Sin permiso', 'No podés proponer evaluación.');
        return;
      }
      await setStatus(quoteId, 'visit_proposed', {
        message: 'Te proponemos una evaluación en el local. Armamos un horario.',
      });
      showSuccess('Le propusiste la evaluación');
    }, 'No se pudo proponer evaluación.');

  const handleIssue = () =>
    void run(async () => {
      if (!canSend) {
        showError('Sin permiso', 'No podés emitir cotizaciones.');
        return;
      }
      const priceNum = Number(price.replace(',', '.'));
      const dur = Number(duration);
      const sess = Number(sessions);
      if (!Number.isFinite(priceNum) || priceNum < 0) throw new Error('Precio inválido');
      if (!Number.isInteger(dur) || dur < 5) throw new Error('Duración inválida: debe ser un número entero de minutos');
      if (!Number.isInteger(sess) || sess < 1) throw new Error('Sesiones inválidas: debe ser un número entero mayor a 0');
      let depositNum: number | null = null;
      if (planPaymentMode === 'deposit') {
        depositNum = Number(planDepositAmount.replace(',', '.'));
        if (!Number.isFinite(depositNum) || depositNum <= 0) {
          throw new Error('Indicá el monto de la seña');
        }
      }
      await issueQuote({
        quoteId,
        pricingMode,
        price: priceNum,
        sessionDurationMinutes: dur,
        estimatedSessions: sess,
        quoteNote: quoteNote.trim() || null,
        planPaymentMode,
        planDepositAmount: depositNum,
      });
      setShowIssue(false);
      showSuccess('Plan emitido');
    }, 'No se pudo emitir el plan.');

  const handleAccept = () => {
    void (async () => {
      if (!canConvert) {
        showError('Sin permiso', 'No podés aceptar cotizaciones.');
        return;
      }
      const ok = await confirm({
        title: '¿Aceptar por el cliente?',
        message: 'Queda aceptada. Es como si el cliente la hubiera confirmado.',
        confirmLabel: 'Aceptar',
      });
      if (!ok) return;
      await run(async () => {
        await acceptAsStore(quoteId);
        showSuccess('Cotización aceptada');
      }, 'No se pudo aceptar.');
    })();
  };

  const handleConvertToWorkOrder = () => {
    void (async () => {
      const ok = await confirm({
        title: '¿Convertir a Orden de Trabajo?',
        message: 'Se creará una OT con el servicio cotizado como primera tarea.',
        confirmLabel: 'Convertir',
      });
      if (!ok) return;
      setBusy(true);
      try {
        const { convertQuoteToWorkOrder } = await import('../infra/quotesRepo');
        const woId = await convertQuoteToWorkOrder(quoteId);
        showSuccess('OT creada', `OT ${woId.slice(0, 8).toUpperCase()}`);
        const { navigateInShell } = await import('../../shell/navigation/rootNavigation');
        navigateInShell(navigation, 'WorkOrderDetail', { workOrderId: woId });
      } catch (e) {
        presentUnexpectedFailure({
          module: 'quotes',
          op: 'quote-convert-ot',
          error: e,
          title: 'Cotización',
          userMessage: 'No se pudo convertir a OT.',
        });
      } finally {
        setBusy(false);
      }
    })();
  };

  const openSchedule = async () => {
    if (!quote) return;
    try {
      const res = await listResources(quote.branch_id);
      const active = res.filter((r) => r.isActive);
      setResources(active);
      setResourceId(active[0]?.id ?? null);
      const d = new Date();
      d.setMinutes(0, 0, 0);
      d.setHours(d.getHours() + 1);
      setScheduleDate(formatIsoDate(d));
      setScheduleTime(toHm(d));
      setResourcePickerOpen(false);
      setShowSchedule(true);
    } catch (e) {
      presentUnexpectedFailure({ module: 'quotes', op: 'agendas', error: e, title: 'Agendas', userMessage: 'No se pudieron cargar agendas.' });
    }
  };

  const handleSchedule = () =>
    void run(async () => {
      if (!canEdit) {
        throw new Error('No podés agendar sesiones.');
      }
      if (!quote || !resourceId) throw new Error('Elegí agenda y horario');
      const startsAt = combineLocalDateAndTime(scheduleDate, scheduleTime);
      if (!startsAt) throw new Error('Horario inválido');
      const progress = computeSessionProgress(
        sessionAppts.map((a) => ({ sessionIndex: a.sessionIndex, status: a.status })),
        quote.estimated_sessions,
      );
      const next = nextSessionIndex(progress) ?? 0;
      await createWalkInSessions({
        quote,
        sessions: [{ resourceId, startsAt: startsAt.toISOString(), sessionIndex: next }],
      });
      setShowSchedule(false);
      showSuccess('Sesión agendada');
    }, 'No se pudo agendar la sesión.');

  const handleComplete = () => {
    void (async () => {
      if (!canEdit) {
        showError('Sin permiso', 'No podés completar planes.');
        return;
      }
      const ok = await confirm({
        title: '¿Completar este plan?',
        message: 'La cotización queda completada.',
        confirmLabel: 'Completar',
      });
      if (!ok) return;
      await run(async () => {
        await setStatus(quoteId, 'completed');
        showSuccess('Plan completado');
      }, 'No se pudo completar.');
    })();
  };

  const handleDecline = () => {
    void (async () => {
      if (!canEdit) {
        showError('Sin permiso', 'No podés rechazar cotizaciones.');
        return;
      }
      const ok = await confirm({
        title: '¿Rechazar esta cotización?',
        message: 'El cliente verá el plan como rechazado. No se puede deshacer.',
        confirmLabel: 'Rechazar',
        destructive: true,
      });
      if (!ok) return;
      await run(async () => {
        await setStatus(quoteId, 'declined', { declineReason: 'Rechazada por el local' });
        showSuccess('Cotización rechazada');
      }, 'No se pudo rechazar.');
    })();
  };

  const handleCancel = () => {
    void (async () => {
      if (!canEdit) {
        showError('Sin permiso', 'No podés cancelar cotizaciones.');
        return;
      }
      const ok = await confirm({
        title: '¿Cancelar esta cotización?',
        message: 'Se cierra. No se puede deshacer.',
        confirmLabel: 'Cancelar',
        destructive: true,
      });
      if (!ok) return;
      await run(async () => {
        await setStatus(quoteId, 'cancelled');
        showSuccess('Cotización cancelada');
      }, 'No se pudo cancelar.');
    })();
  };

  if (!capabilityGate.allowed) {
    return <ShellContentFrame style={styles.center}>{capabilityGate.blocked}</ShellContentFrame>;
  }

  if (loading) {
    return (
      <ShellContentFrame style={styles.center}>
        <Text style={styles.loadingText} testID="quotes.detail.loading">Cargando cotización…</Text>
      </ShellContentFrame>
    );
  }

  if (!quote || loadError) {
    return (
      <ShellContentFrame style={styles.center}>
        <View style={styles.errorBox}>
          <Text style={styles.errorTitle}>{loadError ? 'No se pudo cargar' : 'Cotización no encontrada'}</Text>
          {loadError ? <Text style={styles.errorText}>{loadError}</Text> : null}
          <TouchableOpacity onPress={() => void load()} style={styles.retryBtn}>
            <Text style={styles.retryBtnText}>Reintentar</Text>
          </TouchableOpacity>
        </View>
      </ShellContentFrame>
    );
  }

  const open = !TERMINAL.has(quote.status);
  const canIssue = open && !['accepted', 'scheduled', 'completed'].includes(quote.status);
  const canAccept = quote.status === 'quoted';
  const canScheduleStatus =
    quote.status === 'accepted' ||
    quote.status === 'scheduled' ||
    (quote.status === 'quoted' && quote.schedule_without_accept);
  const hasActiveSessions = sessionAppts.some(
    (a) => a.status === 'requested' || a.status === 'confirmed',
  );
  const canComplete =
    (quote.status === 'accepted' || quote.status === 'scheduled') && !hasActiveSessions;

  const sessionProgress = computeSessionProgress(
    sessionAppts.map((a) => ({ sessionIndex: a.sessionIndex, status: a.status })),
    quote.estimated_sessions,
  );
  const nextSession = nextSessionIndex(sessionProgress);
  const canSchedule = canScheduleStatus && nextSession != null;
  const scheduleCtaLabel = formatScheduleSessionCta(sessionProgress);

  const showSessionProgress =
    quote.pricing_mode != null &&
    (quote.status === 'accepted' ||
      quote.status === 'scheduled' ||
      quote.status === 'completed' ||
      quote.status === 'quoted' ||
      sessionAppts.length > 0);

  const sessionDurationMin = quote.session_duration_minutes ?? 60;

  return (
    <ShellContentFrame style={styles.container}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={90}
      >
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={{ paddingBottom: 16 }}
          refreshControl={
            <RefreshControl
              refreshing={loading}
              onRefresh={() => void load()}
              colors={[ticketColors.gold]}
              tintColor={ticketColors.gold}
            />
          }
        >
          <View style={styles.headerCard}>
            <View style={styles.headerRow}>
              <Text style={styles.title}>Cotización</Text>
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: STATUS_COLORS[quote.status] ?? statusTokens.consultationClosed },
                ]}
                testID="quotes.detail.status"
              >
                <Text style={styles.statusText}>{STATUS_LABELS[quote.status] ?? quote.status}</Text>
              </View>
            </View>
            <Text style={styles.date}>{formatLocalDateTime(quote.created_at)}</Text>
            {quote.pricing_mode ? (
              <Text style={styles.planLine}>
                {quote.pricing_mode === 'per_session'
                  ? `${formatMoney(quote.price_per_session ?? 0)} / sesión`
                  : formatMoney(quote.quoted_total ?? 0)}
                {' · '}
                {quote.estimated_sessions} sesión{quote.estimated_sessions === 1 ? '' : 'es'}
                {quote.session_duration_minutes ? ` · ${quote.session_duration_minutes} min` : ''}
              </Text>
            ) : null}
            {quote.pricing_mode && quote.plan_payment_mode === 'deposit' && quote.plan_deposit_amount != null ? (
              <Text style={styles.planNote}>
                Seña del plan: {formatMoney(quote.plan_deposit_amount)} (1ª sesión)
              </Text>
            ) : null}
            {quote.pricing_mode && quote.plan_payment_mode === 'full' ? (
              <Text style={styles.planNote}>Cobra el total de cada sesión por internet.</Text>
            ) : null}
            {quote.pricing_mode && quote.plan_payment_mode === 'none' ? (
              <Text style={styles.planNote}>Reserva sin pagar por internet.</Text>
            ) : null}
            {quote.quote_note ? <Text style={styles.planNote}>{quote.quote_note}</Text> : null}
          </View>

          {showSessionProgress ? (
            <View style={styles.section} accessibilityLabel="Progreso de sesiones">
              <Text style={styles.sectionTitle}>Turnos del plan</Text>
              <Text style={styles.progressHeadline}>
                {sessionProgress.bookedCount}/{sessionProgress.total} sesiones
              </Text>
              <Text style={styles.progressSub}>
                {sessionProgress.bookedCount === 0
                  ? 'Todavía no hay turnos.'
                  : sessionProgress.bookedCount >= sessionProgress.total
                    ? 'Todas las sesiones del plan tienen turno.'
                    : `${sessionProgress.total - sessionProgress.bookedCount} pendiente${
                        sessionProgress.total - sessionProgress.bookedCount === 1 ? '' : 's'
                      }.`}
              </Text>
              {sessionProgress.total > 1 ? (
                <View style={styles.progressTrack} accessibilityRole="progressbar">
                  {Array.from({ length: sessionProgress.total }, (_, i) => {
                    const idx = i + 1;
                    const filled = sessionProgress.bookedIndexes.includes(idx);
                    return (
                      <View
                        key={idx}
                        style={[styles.progressSeg, filled ? styles.progressSegFilled : null]}
                      />
                    );
                  })}
                </View>
              ) : null}
            </View>
          ) : null}

          {showSessionProgress ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Sesiones</Text>
              {sessionAppts.length === 0 ? (
                <Text style={styles.emptyAppts}>
                  Todavía no hay turnos. Agendá la primera sesión.
                </Text>
              ) : (
                sessionAppts.map((a, i) => (
                  <View
                    key={a.id}
                    style={[styles.apptRow, i > 0 ? styles.apptRowBorder : null]}
                  >
                    <View style={styles.apptMain}>
                      <Text style={styles.apptWhen}>
                        {new Date(a.startsAt).toLocaleDateString('es-AR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                        })}
                        {' · '}
                        {new Date(a.startsAt).toLocaleTimeString('es-AR', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                      </Text>
                      <Text style={styles.apptStatus}>
                        {APPT_STATUS_LABELS[a.status] ?? a.status}
                      </Text>
                    </View>
                    <Text style={styles.apptSession}>
                      Sesión{' '}
                      {a.sessionIndex != null
                        ? `${a.sessionIndex}/${sessionProgress.total}`
                        : `—/${sessionProgress.total}`}
                    </Text>
                  </View>
                ))
              )}
            </View>
          ) : null}

          {evalAppt ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Evaluación</Text>
              <Text style={styles.progressSub}>No cuenta en las sesiones del plan.</Text>
              <View style={[styles.apptRow, { marginTop: 8 }]}>
                <View style={styles.apptMain}>
                  <Text style={styles.apptWhen}>
                    {new Date(evalAppt.startsAt).toLocaleDateString('es-AR', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    })}
                    {' · '}
                    {new Date(evalAppt.startsAt).toLocaleTimeString('es-AR', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}
                  </Text>
                  <Text style={styles.apptStatus}>
                    {APPT_STATUS_LABELS[evalAppt.status] ?? evalAppt.status}
                  </Text>
                </View>
                <Text style={styles.apptSession}>Evaluación</Text>
              </View>
            </View>
          ) : null}

          <View style={styles.section}>
            <Text style={styles.sectionTitle} testID="quotes.detail.section.cliente">Cliente</Text>
            <Text style={styles.customerName}>{quote.customer_name}</Text>
            {quote.customer_phone ? (
              <Text style={styles.customerPhone}>{formatArPhoneDisplay(quote.customer_phone)}</Text>
            ) : null}
            {quote.customer_email ? <Text style={styles.customerPhone}>{quote.customer_email}</Text> : null}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle} testID="quotes.detail.section.servicio">Servicio</Text>
            <Text style={styles.customerName}>{quoteServiceDisplayName(quote.product_name)}</Text>
            {quote.note.trim() ? (
              <>
                <Text style={[styles.sectionTitle, { marginTop: 12 }]}>Pedido</Text>
                <Text style={styles.messageText}>{quote.note}</Text>
              </>
            ) : null}
          </View>

          {photoUrls.length > 0 ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Fotos</Text>
              {photoUrls.map((uri) => (
                <ZoomableImage key={uri} uri={uri} title="Foto" style={styles.photo} />
              ))}
            </View>
          ) : null}

          {messages.length > 0 ? (
            <View style={styles.threadSection}>
              <Text style={styles.sectionTitle} testID="quotes.detail.section.mensajes">Mensajes</Text>
              {messages.map((msg) => {
                const isStore = msg.author === 'store';
                return (
                  <View
                    key={msg.id}
                    style={[
                      styles.messageBubble,
                      isStore ? styles.storeBubble : styles.customerBubble,
                    ]}
                  >
                    <Text
                      style={[
                        styles.messageAuthor,
                        isStore ? styles.messageAuthorStore : styles.messageAuthorCustomer,
                      ]}
                    >
                      {isStore ? 'Local' : quote.customer_name}
                    </Text>
                    <Text style={styles.messageBody}>{msg.body}</Text>
                    <Text style={styles.messageTime}>
                      {new Date(msg.created_at).toLocaleTimeString('es-AR', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </Text>
                  </View>
                );
              })}
            </View>
          ) : null}

          {open ? (
            <View style={styles.actions}>
              {canEdit && (canIssue || quote.status === 'requested' || quote.status === 'needs_info') ? (
                <ActionBtn label="Pedir info" onPress={handleNeedsInfo} disabled={busy} />
              ) : null}
              {canEdit && canIssue ? (
                <ActionBtn label="Proponer evaluación" onPress={handleProposeVisit} disabled={busy} />
              ) : null}
              {canSend && canIssue ? (
                <ActionBtn label="Emitir plan" onPress={() => setShowIssue(true)} disabled={busy} primary />
              ) : null}
              {canConvert && canAccept ? (
                <ActionBtn label="Aceptar por el cliente" onPress={handleAccept} disabled={busy} primary />
              ) : null}
              {canEdit && hasWorkOrdersCap && (quote.status === 'quoted' || quote.status === 'accepted') ? (
                <ActionBtn label="Convertir a OT" onPress={handleConvertToWorkOrder} disabled={busy} primary />
              ) : null}
              {canEdit && canSchedule ? (
                <ActionBtn
                  label={scheduleCtaLabel}
                  onPress={() => void openSchedule()}
                  disabled={busy}
                  primary
                />
              ) : null}
              {canEdit && canComplete ? (
                <ActionBtn label="Completar plan" onPress={handleComplete} disabled={busy} />
              ) : null}
              {canEdit && open && quote.status !== 'scheduled' ? (
                <ActionBtn label="Rechazar" onPress={handleDecline} disabled={busy} danger />
              ) : null}
              {canEdit && open ? (
                <ActionBtn label="Cancelar" onPress={handleCancel} disabled={busy} danger />
              ) : null}
            </View>
          ) : null}
        </ScrollView>

        {open && canReply ? (
          <View style={styles.replyBar}>
            <TextInput
              style={styles.replyInput}
              placeholder="Escribí un mensaje…"
              value={reply}
              onChangeText={setReply}
              multiline
              maxLength={5000}
              testID="quotes.detail.reply-input"
            />
            <TouchableOpacity
              style={[styles.sendBtn, (!reply.trim() || busy) && styles.sendBtnDisabled]}
              onPress={() => void handleReply()}
              disabled={!reply.trim() || busy}
              accessibilityRole="button"
              accessibilityLabel="Enviar mensaje"
              testID="quotes.detail.send"
            >
              <Text style={styles.sendBtnText}>{busy ? '…' : 'Enviar'}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </KeyboardAvoidingView>

      <Modal visible={showIssue} animationType={isDesktop ? 'fade' : 'slide'} transparent onRequestClose={() => setShowIssue(false)}>
        <View style={[styles.modalBackdrop, isDesktop && { justifyContent: 'center', alignItems: 'center' }]}>
          {isDesktop ? <Pressable style={StyleSheet.absoluteFill} onPress={() => setShowIssue(false)} /> : null}
          <View style={[styles.modalCard, isDesktop && { maxWidth: 520, borderRadius: 16 }]}>
            <Text style={styles.modalTitle} testID="quotes.detail.issue-modal.title">Emitir plan</Text>
            <View style={styles.modeRow}>
              {(['per_session', 'total'] as const).map((m) => (
                <TouchableOpacity
                  key={m}
                  style={[styles.modeChip, pricingMode === m && styles.modeChipActive]}
                  onPress={() => setPricingMode(m)}
                >
                  <Text style={[styles.modeChipText, pricingMode === m && styles.modeChipTextActive]}>
                    {m === 'per_session' ? 'Por sesión' : 'Total'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput
              style={styles.field}
              placeholder={pricingMode === 'per_session' ? 'Precio por sesión' : 'Total'}
              keyboardType="numeric"
              value={price}
              onChangeText={setPrice}
            />
            <TextInput
              style={styles.field}
              placeholder="Duración (minutos)"
              keyboardType="numeric"
              value={duration}
              onChangeText={setDuration}
            />
            <TextInput
              style={styles.field}
              placeholder="Sesiones"
              keyboardType="numeric"
              value={sessions}
              onChangeText={setSessions}
            />
            <TextInput
              style={[styles.field, { minHeight: 64 }]}
              placeholder="Nota del plan (opcional)"
              value={quoteNote}
              onChangeText={setQuoteNote}
              multiline
            />
            <Text style={styles.sectionTitle}>Cobro al reservar</Text>
            <View style={styles.modeRow}>
              {(
                [
                  ['inherit', 'Como el local'],
                  ['none', 'Sin cobro'],
                  ['deposit', 'Seña'],
                  ['full', 'Toda la sesión'],
                ] as const
              ).map(([mode, label]) => (
                <TouchableOpacity
                  key={mode}
                  style={[styles.modeChip, planPaymentMode === mode && styles.modeChipActive]}
                  onPress={() => setPlanPaymentMode(mode)}
                >
                  <Text style={[styles.modeChipText, planPaymentMode === mode && styles.modeChipTextActive]}>
                    {label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {planPaymentMode === 'deposit' ? (
              <>
                <TextInput
                  style={styles.field}
                  placeholder="Monto de seña (ARS)"
                  keyboardType="numeric"
                  value={planDepositAmount}
                  onChangeText={setPlanDepositAmount}
                />
                {Number(planDepositAmount.replace(',', '.')) > 0 ? (
                  <Text style={styles.hint}>
                    Al reservar la 1ª sesión: {formatMoney(Number(planDepositAmount.replace(',', '.')))}
                  </Text>
                ) : null}
              </>
            ) : null}
            {planPaymentMode === 'full' && Number(price.replace(',', '.')) > 0 ? (
              <Text style={styles.hint}>
                Al reservar cada sesión:{' '}
                {formatMoney(
                  pricingMode === 'total' && Number(sessions) > 0
                    ? Number(price.replace(',', '.')) / Number(sessions)
                    : Number(price.replace(',', '.')),
                )}
              </Text>
            ) : null}
            {planPaymentMode === 'inherit' ? (
              <Text style={styles.hint}>Usa la seña del servicio o del local.</Text>
            ) : null}
            {planPaymentMode === 'none' ? (
              <Text style={styles.hint}>El cliente agenda sin pagar por internet.</Text>
            ) : null}
            <View style={styles.modalActions}>
              <TouchableOpacity onPress={() => setShowIssue(false)}>
                <Text style={styles.quietBtn}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.primaryBtn} onPress={() => void handleIssue()} disabled={busy} testID="quotes.detail.issue-modal.emit">
                <Text style={styles.primaryBtnText}>Emitir</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={showSchedule} animationType={isDesktop ? 'fade' : 'slide'} transparent onRequestClose={() => {
        setResourcePickerOpen(false);
        setShowSchedule(false);
      }}>
        <View style={[styles.modalBackdrop, isDesktop && { justifyContent: 'center', alignItems: 'center' }]}>
          {isDesktop ? (
            <Pressable style={StyleSheet.absoluteFill} onPress={() => {
              setResourcePickerOpen(false);
              setShowSchedule(false);
            }} />
          ) : null}
          <View style={[styles.modalCard, isDesktop && { maxWidth: 520, borderRadius: 16 }]}>
            <Text style={styles.modalTitle}>{scheduleCtaLabel}</Text>

            <Text style={styles.fieldLabel}>Agenda</Text>
            <TouchableOpacity
              style={[styles.pick, resourcePickerOpen && styles.pickExpanded, resources.length === 0 && styles.pickDisabled]}
              onPress={() => setResourcePickerOpen(true)}
              disabled={resources.length === 0 || singleAgenda}
              accessibilityRole="button"
              accessibilityState={{ disabled: resources.length === 0 || singleAgenda, expanded: resourcePickerOpen }}
              accessibilityLabel={`Agenda: ${selectedResource?.displayName ?? 'Elegí una agenda'}`}
            >
              <View style={styles.pickMain}>
                <Text style={styles.pickV} numberOfLines={1}>
                  {selectedResource?.displayName ?? (resources.length === 0 ? 'Sin agendas activas' : 'Elegí una agenda')}
                </Text>
                {resources.length === 0 || singleAgenda ? (
                  <Text style={styles.pickSub} numberOfLines={1}>
                    {resources.length === 0
                      ? 'Configurá las agendas primero'
                      : 'Agenda de la sucursal'}
                  </Text>
                ) : null}
              </View>
              {singleAgenda ? (
                <Text style={styles.pickSoloHint}>La única</Text>
              ) : (
                <Ionicons name="chevron-down" size={18} color={ticketColors.inkLight} />
              )}
            </TouchableOpacity>

            <Text style={[styles.fieldLabel, { marginTop: 4 }]}>Fecha</Text>
            <InventoryIsoDateField
              value={scheduleDate}
              onChange={setScheduleDate}
              emptyLabel="Elegí la fecha"
              ariaLabel="Fecha de la sesión"
              showClearButton={false}
            />

            <Text style={[styles.fieldLabel, { marginTop: 4 }]}>Hora</Text>
            <HmTimeField
              value={scheduleTime}
              onChange={setScheduleTime}
              accessibilityLabel="Hora de inicio"
              comfortable
            />

            <Text style={styles.hint}>Duración: {sessionDurationMin} min</Text>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.outlineBtn}
                onPress={() => {
                  setResourcePickerOpen(false);
                  setShowSchedule(false);
                }}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel="Cancelar"
              >
                <Text style={styles.outlineBtnText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.primaryBtn, styles.primaryBtnGrow, busy && styles.sendBtnDisabled]}
                onPress={() => void handleSchedule()}
                disabled={busy || !resourceId || !scheduleDate || !scheduleTime}
                accessibilityRole="button"
                accessibilityLabel="Agendar"
              >
                <Text style={styles.primaryBtnText}>Agendar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <MasterFilterSheetModal
        visible={showSchedule && resourcePickerOpen}
        title="Elegir agenda"
        options={resourceOptions}
        selectedId={resourceId}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) setResourceId(id);
          setResourcePickerOpen(false);
        }}
        onClose={() => setResourcePickerOpen(false)}
      />
      {ConfirmModal}
    </ShellContentFrame>
  );
}

function ActionBtn({
  label,
  onPress,
  disabled,
  primary,
  danger,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <TouchableOpacity
      style={[
        styles.actionBtn,
        primary && styles.actionBtnPrimary,
        danger && styles.actionBtnDanger,
        disabled && styles.sendBtnDisabled,
      ]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      testID={`quotes.detail.action.${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Text
        style={[
          styles.actionBtnText,
          primary && styles.actionBtnTextPrimary,
          danger && styles.actionBtnTextDanger,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: ticketColors.paper },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { fontSize: 16, color: ticketColors.inkLight },
  errorBox: { padding: 24, alignItems: 'center', width: '100%' },
  errorTitle: { fontSize: 18, fontWeight: '700', color: ticketColors.ink, marginBottom: 8, textAlign: 'center' },
  errorText: { fontSize: 14, color: ticketColors.inkLight, marginBottom: 16, textAlign: 'center' },
  retryBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  retryBtnText: { color: ticketColors.goldInk, fontSize: 15, fontWeight: '700' },
  headerCard: {
    backgroundColor: ticketColors.card,
    borderRadius: 10,
    padding: 16,
    margin: 12,
    borderWidth: 1,
    borderColor: ticketColors.border,
  },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: 18, fontWeight: '700', color: ticketColors.ink, flex: 1, marginRight: 8 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  statusText: { fontSize: 11, fontWeight: '700', color: '#fff' },
  date: { fontSize: 12, color: ticketColors.inkLight, marginTop: 4 },
  planLine: { fontSize: 14, fontWeight: '600', color: ticketColors.ink, marginTop: 8 },
  planNote: { fontSize: 13, color: ticketColors.inkLight, marginTop: 4 },
  progressHeadline: { fontSize: 16, fontWeight: '700', color: ticketColors.ink, marginTop: 2 },
  progressSub: { fontSize: 13, color: ticketColors.inkLight, marginTop: 4, lineHeight: 18 },
  progressTrack: { flexDirection: 'row', gap: 4, marginTop: 10 },
  progressSeg: {
    flex: 1,
    height: 6,
    borderRadius: 999,
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.border,
  },
  progressSegFilled: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  emptyAppts: { fontSize: 13, color: ticketColors.inkLight, lineHeight: 18, marginTop: 2 },
  apptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingVertical: 10,
  },
  apptRowBorder: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.border,
  },
  apptMain: { flex: 1, minWidth: 0 },
  apptWhen: { fontSize: 14, fontWeight: '600', color: ticketColors.ink },
  apptStatus: { fontSize: 12, color: ticketColors.inkLight, marginTop: 2 },
  apptSession: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink2,
    flexShrink: 0,
  },
  section: {
    backgroundColor: ticketColors.card,
    borderRadius: 10,
    padding: 16,
    marginHorizontal: 12,
    marginTop: 8,
    borderWidth: 1,
    borderColor: ticketColors.border,
  },
  threadSection: { padding: 16, marginHorizontal: 12, marginTop: 8 },
  sectionTitle: { fontSize: 13, fontWeight: '700', color: ticketColors.inkLight, marginBottom: 4 },
  customerName: { fontSize: 16, fontWeight: '600', color: ticketColors.ink },
  customerPhone: { fontSize: 14, color: ticketColors.inkLight, marginTop: 2 },
  messageText: { fontSize: 14, color: ticketColors.ink },
  photo: { width: '100%', height: 180, borderRadius: 8, marginTop: 8 },
  messageBubble: { borderRadius: 10, padding: 12, marginBottom: 8, maxWidth: '85%' },
  /** Soft brand tint + left accent — readable body on light (portal chat pattern). */
  storeBubble: {
    alignSelf: 'flex-end',
    backgroundColor: colors.accentSoft,
    borderWidth: 1,
    borderColor: brand[100],
    borderLeftWidth: 3,
    borderLeftColor: ticketColors.gold,
  },
  customerBubble: {
    backgroundColor: ticketColors.card,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  messageAuthor: { fontSize: 11, fontWeight: '700', marginBottom: 2 },
  messageAuthorStore: { color: ticketColors.gold },
  messageAuthorCustomer: { color: ticketColors.ink2 },
  messageBody: { fontSize: 14, lineHeight: 20, color: ticketColors.ink },
  messageTime: { fontSize: 10, color: ticketColors.ink3, marginTop: 4, textAlign: 'right' },
  actions: { paddingHorizontal: 12, marginTop: 12, gap: 8 },
  actionBtn: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: ticketColors.card,
  },
  actionBtnPrimary: { backgroundColor: ticketColors.gold, borderColor: ticketColors.gold },
  actionBtnDanger: { borderColor: ticketColors.ruleSoft },
  actionBtnText: { fontSize: 14, fontWeight: '600', color: ticketColors.ink, textAlign: 'center' },
  actionBtnTextPrimary: { color: ticketColors.goldInk },
  actionBtnTextDanger: { color: ticketColors.bad },
  replyBar: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    backgroundColor: ticketColors.paper2,
    borderTopWidth: 1,
    borderTopColor: ticketColors.border,
  },
  replyInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: ticketColors.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    maxHeight: 80,
    backgroundColor: ticketColors.card,
  },
  sendBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 16,
    justifyContent: 'center',
  },
  sendBtnDisabled: { opacity: 0.5 },
  sendBtnText: { color: ticketColors.goldInk, fontSize: 16, fontWeight: '700' },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  modalCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 20,
    gap: 10,
  },
  modalTitle: { fontSize: 18, fontWeight: '700', color: ticketColors.ink },
  modeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  modeChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: ticketColors.paper2,
  },
  modeChipActive: { backgroundColor: ticketColors.gold },
  modeChipText: { fontSize: 12, fontWeight: '600', color: ticketColors.ink },
  modeChipTextActive: { color: ticketColors.goldInk },
  field: {
    borderWidth: 1,
    borderColor: ticketColors.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    backgroundColor: ticketColors.card,
    color: ticketColors.ink,
  },
  hint: { fontSize: 12, color: ticketColors.inkLight },
  fieldLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: ticketColors.inkLight,
    marginBottom: 6,
  },
  pick: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: ticketColors.border,
    borderRadius: 10,
    backgroundColor: ticketColors.card,
    paddingVertical: 11,
    paddingHorizontal: 12,
  },
  pickExpanded: {
    borderColor: brand[500],
    backgroundColor: colors.accentSoft,
  },
  pickDisabled: { opacity: 0.55 },
  pickMain: { flex: 1, minWidth: 0, gap: 1 },
  pickV: { fontSize: 14, fontWeight: '700', color: ticketColors.ink },
  pickSub: { fontSize: 11.5, fontWeight: '500', color: ticketColors.inkLight },
  pickSoloHint: { fontSize: 11, fontWeight: '600', color: ticketColors.inkLight },
  modalActions: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8 },
  quietBtn: { fontSize: 14, color: ticketColors.inkLight, padding: 8 },
  outlineBtn: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.border,
    backgroundColor: ticketColors.card,
    minHeight: 44,
  },
  outlineBtnText: { fontSize: 14, fontWeight: '600', color: ticketColors.ink },
  primaryBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  primaryBtnGrow: { flex: 1, alignItems: 'center', minHeight: 44, justifyContent: 'center' },
  primaryBtnText: { color: ticketColors.goldInk, fontWeight: '700', fontSize: 15 },
});
